import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Search, MoreHorizontal, ChevronLeft, ChevronRight, Phone, UserPlus, MessageSquare, Bell, Download, Filter, X, RefreshCw, Mail, Store, MapPin, Check } from 'lucide-react';
import ProspectDrawer from '@/components/ProspectDrawer';
import { EmailHistoryBadge } from '@/components/ProspectionEmailDialog';
import ExportModal from '@/components/ExportModal';
import { formatCA } from './CampaignDetailPage';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const STATUS_LABELS = {
  en_attente: 'En attente', trouve: 'Trouvé', a_verifier: 'À vérifier',
  non_trouve: 'Non trouvé', ignore: 'Ignoré', valide_manuellement: 'Validé'
};
const COMMERCIAL_LABELS = {
  nouveau: 'Nouveau', a_appeler: 'À appeler', appele: 'Appelé', rdv_obtenu: 'RDV obtenu',
  en_negociation: 'En négociation', refus: 'Refus', rappel_demande: 'Rappel', injoignable: 'Injoignable',
  mauvais_numero: 'Mauvais n°', converti: 'Converti',
  mail_prospection_envoye: 'Mail Prospection envoyé', mail_doc_envoye: 'Mail Doc envoyé',
};

export default function ProspectsPage() {
  const { user } = useAuth();
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ statut_recherche: '', statut_commercial: '', assigne_a: '', enseigne: [], departement: [] });
  const [loading, setLoading] = useState(true);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [commerciaux, setCommerciaux] = useState([]);
  const [facets, setFacets] = useState({ enseignes: [], departements: [] });
  const [campaignsMap, setCampaignsMap] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCampaignId, setBulkCampaignId] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const location = useLocation();

  // Auto-open prospect drawer if ?prospect=id in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get('prospect');
    if (pid && (!selectedProspect || selectedProspect.id !== pid)) {
      api.get(`/prospects/${pid}`).then(r => setSelectedProspect(r.data)).catch(() => {});
    }
  }, [location.search]); // eslint-disable-line

  // Load campaigns once to resolve concurrent name per prospect
  useEffect(() => {
    api.get('/campaigns').then(r => {
      const map = {};
      (r.data || []).forEach(c => { map[c.id] = c; });
      setCampaignsMap(map);
    }).catch(() => {});
  }, []);

  const loadProspects = useCallback(async () => {
    setLoading(true);
    const params = { page, per_page: perPage, sort_by: 'score_confiance', sort_order: 'desc' };
    if (search) params.search = search;
    if (filters.statut_recherche) params.statut_recherche = filters.statut_recherche;
    if (filters.statut_commercial) params.statut_commercial = filters.statut_commercial;
    if (filters.assigne_a) params.assigne_a = filters.assigne_a;
    if (filters.enseigne && filters.enseigne.length) params.enseigne = filters.enseigne.join(',');
    if (filters.departement && filters.departement.length) params.departement = filters.departement.join(',');
    try {
      const { data } = await api.get('/prospects', { params });
      setProspects(data.prospects);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [page, perPage, search, filters]);

  useEffect(() => { loadProspects(); }, [loadProspects]);
  useEffect(() => {
    api.get('/users/commerciaux').then(r => setCommerciaux(r.data)).catch(() => {});
    api.get('/prospects/filters/facets').then(r => setFacets(r.data)).catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / perPage);

  const reSearchProspect = async (e, prospectId) => {
    e.stopPropagation();
    try {
      toast.info('Recherche en cours...');
      await api.post(`/prospects/${prospectId}/re-search`);
      toast.success('Recherche terminée');
      loadProspects();
    } catch { toast.error('Erreur de recherche'); }
  };

  const quickAssign = async (prospectId, userId) => {
    try {
      await api.post(`/prospects/${prospectId}/assign`, { user_id: userId });
      toast.success('Prospect assigné');
      loadProspects();
    } catch { toast.error("Erreur d'assignation"); }
  };

  const quickCallResult = async (prospectId, resultat) => {
    try {
      await api.post(`/prospects/${prospectId}/call-result`, { resultat });
      toast.success('Résultat enregistré');
      loadProspects();
    } catch { toast.error('Erreur'); }
  };

  const clearFilters = () => {
    setFilters({ statut_recherche: '', statut_commercial: '', assigne_a: '', enseigne: [], departement: [] });
    setSearch('');
    setPage(1);
  };

  const hasFilters = search || filters.statut_recherche || filters.statut_commercial || filters.assigne_a || (filters.enseigne && filters.enseigne.length) || (filters.departement && filters.departement.length);

  const toggleEnseigne = (e) => {
    setFilters(f => ({ ...f, enseigne: f.enseigne.includes(e) ? f.enseigne.filter(x => x !== e) : [...f.enseigne, e] }));
    setPage(1);
  };
  const toggleDept = (d) => {
    setFilters(f => ({ ...f, departement: f.departement.includes(d) ? f.departement.filter(x => x !== d) : [...f.departement, d] }));
    setPage(1);
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="prospects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Prospects</h1>
          <p className="text-sm text-[#71717A] mt-0.5">{total.toLocaleString()} prospect{total > 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn"
            className="h-9 gap-1.5 border-[#E5E7EB]">
            <Filter className="w-3.5 h-3.5" /> Filtres
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowExport(true)} data-testid="export-btn"
            className="h-9 gap-1.5 border-[#E5E7EB]">
            <Download className="w-3.5 h-3.5" /> Exporter
          </Button>
        </div>
      </div>

      {/* Quick filter presets */}
      <div className="flex gap-2 flex-wrap" data-testid="quick-filters">
        {[
          { label: 'Tous', fn: () => clearFilters() },
          { label: 'Sans téléphone', fn: () => { setFilters(f => ({ ...f, statut_recherche: 'non_trouve' })); setPage(1); } },
          { label: 'À appeler', fn: () => { setFilters(f => ({ ...f, statut_commercial: 'a_appeler' })); setPage(1); } },
          { label: 'Rappels', fn: () => { setFilters(f => ({ ...f, statut_commercial: 'rappel_demande' })); setPage(1); } },
          { label: 'RDV obtenus', fn: () => { setFilters(f => ({ ...f, statut_commercial: 'rdv_obtenu' })); setPage(1); } },
          { label: 'Convertis', fn: () => { setFilters(f => ({ ...f, statut_commercial: 'converti' })); setPage(1); } },
          { label: 'Injoignables', fn: () => { setFilters(f => ({ ...f, statut_commercial: 'injoignable' })); setPage(1); } },
        ].map(f => (
          <Button key={f.label} variant="outline" size="sm" onClick={f.fn}
            className="h-7 text-xs border-[#E5E7EB] hover:border-[#F97316] hover:text-[#F97316]">
            {f.label}
          </Button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input placeholder="Rechercher (enseigne, magasin, ville, téléphone...)" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="prospects-search"
            className="pl-10 h-10 bg-white border-[#E5E7EB]" />
        </div>
        {showFilters && (
          <div className="flex flex-wrap gap-3 p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]" data-testid="filters-panel">
            <Select value={filters.statut_recherche} onValueChange={v => { setFilters(f => ({ ...f, statut_recherche: v === 'all' ? '' : v })); setPage(1); }}>
              <SelectTrigger className="w-48 h-9 bg-white" data-testid="filter-statut-recherche"><SelectValue placeholder="Statut recherche" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.statut_commercial} onValueChange={v => { setFilters(f => ({ ...f, statut_commercial: v === 'all' ? '' : v })); setPage(1); }}>
              <SelectTrigger className="w-48 h-9 bg-white" data-testid="filter-statut-commercial"><SelectValue placeholder="Statut commercial" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.entries(COMMERCIAL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <Select value={filters.assigne_a} onValueChange={v => { setFilters(f => ({ ...f, assigne_a: v === 'all' ? '' : v })); setPage(1); }}>
                <SelectTrigger className="w-48 h-9 bg-white" data-testid="filter-assigne"><SelectValue placeholder="Assigné à" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {commerciaux.map(c => <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-white min-w-[140px] justify-start" data-testid="filter-enseigne-btn">
                  <Store className="w-3.5 h-3.5 text-[#71717A]" />
                  <span className="text-xs">
                    {filters.enseigne.length === 0 ? 'Enseigne' : `${filters.enseigne.length} enseigne${filters.enseigne.length > 1 ? 's' : ''}`}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-xs font-semibold">Filtrer par enseigne</span>
                  {filters.enseigne.length > 0 && (
                    <button onClick={() => { setFilters(f => ({ ...f, enseigne: [] })); setPage(1); }} className="text-xs text-[#F97316] hover:underline">
                      Tout effacer
                    </button>
                  )}
                </div>
                <ScrollArea className="h-64">
                  <div className="space-y-0.5">
                    {(facets.enseignes || []).length === 0 && <p className="text-xs text-[#71717A] p-2">Aucune enseigne</p>}
                    {(facets.enseignes || []).map(e => (
                      <button
                        key={e.value}
                        onClick={() => toggleEnseigne(e.value)}
                        className={`w-full text-left flex items-center justify-between px-2 py-1.5 rounded hover:bg-[#F4F4F5] text-sm ${filters.enseigne.includes(e.value) ? 'bg-[#FFF7ED]' : ''}`}
                        data-testid={`filter-enseigne-option-${e.value}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded border flex items-center justify-center ${filters.enseigne.includes(e.value) ? 'bg-[#F97316] border-[#F97316]' : 'border-[#D1D5DB]'}`}>
                            {filters.enseigne.includes(e.value) && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="truncate">{e.value}</span>
                        </span>
                        <span className="text-xs text-[#71717A] ml-2">{e.count}</span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-white min-w-[140px] justify-start" data-testid="filter-dept-btn">
                  <MapPin className="w-3.5 h-3.5 text-[#71717A]" />
                  <span className="text-xs">
                    {filters.departement.length === 0 ? 'Département' : `${filters.departement.length} dépt${filters.departement.length > 1 ? 's' : ''}`}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-xs font-semibold">Filtrer par département</span>
                  {filters.departement.length > 0 && (
                    <button onClick={() => { setFilters(f => ({ ...f, departement: [] })); setPage(1); }} className="text-xs text-[#F97316] hover:underline">
                      Tout effacer
                    </button>
                  )}
                </div>
                <ScrollArea className="h-64">
                  <div className="grid grid-cols-3 gap-1">
                    {(facets.departements || []).length === 0 && <p className="text-xs text-[#71717A] p-2 col-span-3">Aucun département</p>}
                    {(facets.departements || []).map(d => (
                      <button
                        key={d.value}
                        onClick={() => toggleDept(d.value)}
                        className={`flex items-center justify-between px-2 py-1.5 rounded hover:bg-[#F4F4F5] text-xs ${filters.departement.includes(d.value) ? 'bg-[#FFF7ED] border border-[#F97316]' : 'border border-transparent'}`}
                        data-testid={`filter-dept-option-${d.value}`}
                      >
                        <span className="font-mono font-semibold">{d.value}</span>
                        <span className="text-[#71717A]">{d.count}</span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 gap-1 text-[#71717A]">
                <X className="w-3.5 h-3.5" /> Réinitialiser
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk action bar — visible when at least 1 prospect is selected */}
      {selectedIds.size > 0 && (
        <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white rounded-lg p-3 shadow-md flex items-center gap-3 mb-3"
          data-testid="bulk-actions-bar">
          <input type="checkbox" checked readOnly className="accent-white scale-110" />
          <span className="font-semibold text-sm" data-testid="bulk-selected-count">
            {selectedIds.size} prospect{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <select value={bulkCampaignId} onChange={e => setBulkCampaignId(e.target.value)}
              className="h-8 rounded px-2 text-xs text-black bg-white border-0"
              data-testid="bulk-campaign-select">
              <option value="">Choisir une campagne…</option>
              {Object.values(campaignsMap).map(c => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
              <option value="__none__">— Détacher de toute campagne</option>
            </select>
            <button disabled={!bulkCampaignId || bulkApplying} onClick={async () => {
              if (!bulkCampaignId) return;
              const cid = bulkCampaignId === '__none__' ? '' : bulkCampaignId;
              setBulkApplying(true);
              try {
                const ids = Array.from(selectedIds);
                // PUT in parallel, but cap concurrency at 8 to avoid hammering server
                let done = 0;
                const queue = [...ids];
                const worker = async () => {
                  while (queue.length) {
                    const id = queue.shift();
                    try { await api.put(`/prospects/${id}`, { campagne_id: cid }); done++; } catch { /* skip */ }
                  }
                };
                await Promise.all(Array.from({length: 8}, worker));
                toast.success(`${done} prospect${done > 1 ? 's' : ''} ${cid ? 'ajoutés à la campagne' : 'détachés'}`);
                setSelectedIds(new Set());
                setBulkCampaignId('');
                loadProspects();
              } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
              setBulkApplying(false);
            }}
              className="h-8 px-3 bg-white text-[#EA580C] hover:bg-[#FED7AA] rounded text-xs font-semibold disabled:opacity-50"
              data-testid="bulk-apply-campaign-btn">
              {bulkApplying ? 'Application…' : 'Appliquer'}
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="text-white/80 hover:text-white text-xs underline"
              data-testid="bulk-clear-btn">Annuler</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="prospects-table">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <th className="text-left py-3 px-3 w-8">
                <input type="checkbox"
                  checked={prospects.length > 0 && prospects.every(p => selectedIds.has(p.id))}
                  onChange={e => {
                    const next = new Set(selectedIds);
                    if (e.target.checked) prospects.forEach(p => next.add(p.id));
                    else prospects.forEach(p => next.delete(p.id));
                    setSelectedIds(next);
                  }}
                  data-testid="bulk-select-all"
                />
              </th>
              {['Enseigne', 'Adresse', 'CP', 'Ville', 'Téléphone', 'Dirigeant', 'Email', 'CA Aliment.', 'Commercial', 'Concurrent', 'Score', 'Statut', ''].map(h => (
                <th key={h} className="text-left py-3 px-3 text-[#71717A] font-medium text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-[#E5E7EB]/50">
                  {Array.from({ length: 14 }).map((_, j) => (
                    <td key={j} className="py-3 px-3"><div className="h-4 bg-[#F3F4F6] rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : prospects.length === 0 ? (
              <tr><td colSpan={14} className="py-12 text-center text-[#71717A]">Aucun prospect trouvé</td></tr>
            ) : prospects.map(p => {
              const campConcurrent = campaignsMap[p.campagne_id]?.concurrent || '';
              return (
              <tr key={p.id} className="prospect-row border-b border-[#E5E7EB]/50 cursor-pointer"
                onClick={() => setSelectedProspect(p)} data-testid={`prospect-row-${p.id}`}>
                <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={(e) => {
                      const next = new Set(selectedIds);
                      if (e.target.checked) next.add(p.id); else next.delete(p.id);
                      setSelectedIds(next);
                    }}
                    data-testid={`bulk-select-${p.id}`}
                  />
                </td>
                <td className="py-2.5 px-3 font-medium text-xs">{p.enseigne}</td>
                <td className="py-2.5 px-3 max-w-[200px] truncate text-xs text-[#27272A]" title={p.rue}>{p.rue || '-'}</td>
                <td className="py-2.5 px-3 text-[#71717A] text-xs">{p.code_postal}</td>
                <td className="py-2.5 px-3 text-xs">{p.ville}</td>
                <td className="py-2.5 px-3">
                  {p.telephone !== '?' ? (
                    <a href={`tel:${p.telephone?.replace(/\s/g, '')}`} className="font-mono text-xs font-medium hover:text-[#0055FF] underline-offset-2 hover:underline"
                      onClick={e => e.stopPropagation()}>{p.telephone}</a>
                  ) : <span className="text-xs text-[#71717A]">?</span>}
                </td>
                <td className="py-2.5 px-3 text-xs">
                  {(p.prenom_dirigeant || p.nom_dirigeant) ? (
                    <span className="text-[#8B5CF6] font-medium">{p.prenom_dirigeant} {p.nom_dirigeant}</span>
                  ) : <span className="text-[#71717A]">-</span>}
                </td>
                <td className="py-2.5 px-3 text-xs max-w-[180px] truncate">
                  {p.email_professionnel ? (
                    <a href={`mailto:${p.email_professionnel}`} className="text-[#0055FF] hover:underline"
                      onClick={e => e.stopPropagation()} title={p.email_professionnel}>{p.email_professionnel}</a>
                  ) : <span className="text-[#71717A]">-</span>}
                </td>
                <td className="py-2.5 px-3 text-xs whitespace-nowrap">
                  {p.ca_alimentaire_estimatif ? (
                    <span className="font-mono font-semibold text-[#15803D]">{formatCA(p.ca_alimentaire_estimatif)}</span>
                  ) : <span className="text-[#A1A1AA] italic">-</span>}
                </td>
                <td className="py-2.5 px-3 text-xs whitespace-nowrap">
                  {p.commercial_assigne_nom ? (
                    <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium"
                      style={{ background: (p.commercial_assigne_color || '#71717A') + '22', color: p.commercial_assigne_color || '#27272A' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.commercial_assigne_color || '#71717A' }}></span>
                      {p.commercial_assigne_nom}
                    </span>
                  ) : <span className="text-[#A1A1AA] italic">non assigné</span>}
                </td>
                <td className="py-2.5 px-3 text-xs">
                  {campConcurrent ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-[#FEF2F2] text-[#B91C1C] font-medium" title={`Campagne : ${campaignsMap[p.campagne_id]?.nom || ''}`}>
                      ⚔️ {campConcurrent}
                    </span>
                  ) : <span className="text-[#71717A]">-</span>}
                </td>
                <td className="py-2.5 px-3">
                  {p.score_confiance > 0 && (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${p.score_confiance >= 70 ? 'score-high' : p.score_confiance >= 50 ? 'score-medium' : 'score-low'}`}>
                      {p.score_confiance}%
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-3">
                  <Badge variant="secondary" className={`text-xs status-${p.statut_recherche}`}>
                    {STATUS_LABELS[p.statut_recherche] || p.statut_recherche}
                  </Badge>
                  {p.emails_sent_count > 0 && (
                    <div className="mt-1" onClick={e => e.stopPropagation()}>
                      <EmailHistoryBadge prospectId={p.id} count={p.emails_sent_count} />
                    </div>
                  )}
                </td>
                <td className="py-2.5 px-3" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#71717A] hover:text-[#F97316]"
                    onClick={e => reSearchProspect(e, p.id)} data-testid={`re-search-${p.id}`} title="Relancer la recherche">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#71717A]">Page {page} sur {totalPages} ({total} résultats)</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="h-8 w-8 p-0" data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="h-8 w-8 p-0" data-testid="next-page"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {selectedProspect && (
        <ProspectDrawer prospect={selectedProspect} onClose={() => setSelectedProspect(null)}
          onUpdate={loadProspects} commerciaux={commerciaux} />
      )}
      {showExport && <ExportModal onClose={() => setShowExport(false)} commerciaux={commerciaux} />}
    </div>
  );
}
